import { Grid, PasswordInput, Button, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import type { Password } from "../../../types/password";
import { validatePasswordChange } from "../utils/passwordValidation";
import { PasswordRequirementsMessage } from "./PasswordRequirementsMessage";

interface StandardPasswordFormProps {
  password: Password;
  setPassword: React.Dispatch<React.SetStateAction<Password>>;
  updatePassword: (oldPassword: string, newPassword: string) => any;
  setNotification: (notification: {
    title: string;
    message: React.ReactNode;
    color: string;
  }) => void;
  setError: (error: { newPassword: string; confirmPassword: string }) => void;
  error: { newPassword: string; confirmPassword: string };
}

/**
 * Standard password change form component.
 * Handles password change for users with existing passwords.
 */
export const StandardPasswordForm = ({
  password,
  setPassword,
  updatePassword,
  setNotification,
  setError,
  error,
}: StandardPasswordFormProps) => {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validatePasswordChange(
      password?.oldPassword,
      password?.newPassword,
      password?.confirmPassword
    );

    if (validationErrors.newPassword || validationErrors.confirmPassword) {
      setError(validationErrors);
      return;
    }

    setError({ newPassword: "", confirmPassword: "" });

    setNotification({
      title: "Updating Business Info",
      message: "Updating business info...",
      color: "blue",
    });

    try {
      const response = await updatePassword(
        password?.oldPassword,
        password?.newPassword
      );

      if (response?.status === 200) {
        setNotification({
          title: "Success",
          message: "Password changed successfully",
          color: "green",
        });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message;
      if (errorMessage) {
        setNotification({
          title: "Password Requirements",
          message: <PasswordRequirementsMessage errorMessage={errorMessage} />,
          color: "red",
        });
      } else {
        setNotification({
          title: "Error",
          message: "An error occurred while changing password",
          color: "red",
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="change-password">
      <div className="change-password-title">
        <h3>Change Password</h3>
        <Text c="dimmed">Please enter your new password</Text>
      </div>
      <Grid className="change-password-grid">
        <Grid.Col>
          <PasswordInput
            label="Old Password"
            placeholder="Your old password"
            name="oldPassword"
            onChange={(e) =>
              setPassword({
                ...password,
                oldPassword: e.target.value,
              })
            }
            leftSection={<IconLock color="#A3A9B0" />}
            value={password?.oldPassword}
            styles={{
              error: {
                wordBreak: "break-word",
                whiteSpace: "normal",
              },
            }}
          />
        </Grid.Col>
        <Grid.Col>
          <PasswordInput
            label="New Password"
            placeholder="Your new password"
            name="newPassword"
            error={error.newPassword}
            onChange={(e) =>
              setPassword({
                ...password,
                newPassword: e.target.value,
              })
            }
            leftSection={<IconLock color="#A3A9B0" />}
            value={password?.newPassword}
            styles={{
              error: {
                wordBreak: "break-word",
                whiteSpace: "normal",
              },
            }}
          />
        </Grid.Col>
        <Grid.Col>
          <PasswordInput
            label="Confirm New Password"
            placeholder="Re-enter new password"
            name="newPassword"
            error={error.confirmPassword}
            onChange={(e) =>
              setPassword({
                ...password,
                confirmPassword: e.target.value,
              })
            }
            value={password?.confirmPassword}
            leftSection={<IconLock color="#A3A9B0" />}
            styles={{
              error: {
                wordBreak: "break-word",
                whiteSpace: "normal",
              },
            }}
          />
        </Grid.Col>
        <span className="submit-container">
          <Button
            type="submit"
            className="submit"
            color="#0077cc"
            radius={"md"}
          >
            Change password
          </Button>
        </span>
      </Grid>
    </form>
  );
};
